import { sendLandingPageEmail } from './emailService';
import { supabase } from './supabase';
/* NCR Revenue OS intake endpoint. When CRM_INTAKE_URL is configured at build time the
   forms post directly to the CRM; otherwise (or when the CRM call fails) they fall back
   to the site's Supabase tables so no submission is ever lost. The site key is a
   publishable identifier, not a secret; the CRM must validate and rate-limit server-side. */
const CRM_INTAKE_URL = process.env.CRM_INTAKE_URL || '';
const CRM_SITE_KEY = process.env.CRM_SITE_KEY || '';
const postToCrm = async (path, payload) => {
    if (!CRM_INTAKE_URL)
        return false;
    try {
        const res = await fetch(`${CRM_INTAKE_URL.replace(/\/$/, '')}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Site-Key': CRM_SITE_KEY },
            body: JSON.stringify(payload)
        });
        return res.ok;
    }
    catch {
        return false;
    }
};
/* Shared submission pipeline for Careers, AI Expert Network and Delivery Partners forms.
   Follows the site's existing integration conventions: Supabase for records (the CRM inbox),
   EmailJS for the internal notification. Individuals land in `talent_applications`,
   companies in `partner_enquiries`. Duplicate handling: a person is keyed by email and a
   partner by website domain; repeat submissions are stored as new linked applications
   (is_returning = true) so history is preserved and no duplicate "person" is created. */
export const CV_MAX_MB = 5;
const CV_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];
const CV_EXTENSIONS = ['.pdf', '.doc', '.docx'];
const CV_BUCKET = 'cv-uploads';
export const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
export const isValidUrl = (v) => {
    try {
        const u = new URL(v.startsWith('http') ? v : `https://${v}`);
        return u.hostname.includes('.');
    }
    catch {
        return false;
    }
};
export const validateCvFile = (file) => {
    const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!CV_EXTENSIONS.includes(ext) || (file.type && !CV_TYPES.includes(file.type))) {
        return 'Please upload a PDF, DOC or DOCX file.';
    }
    if (file.size > CV_MAX_MB * 1024 * 1024) {
        return `File is too large. The limit is ${CV_MAX_MB} MB.`;
    }
    return null;
};
/* Uploads a CV to the private cv-uploads bucket and returns its storage path.
   The bucket must be private; the team opens files via the Supabase dashboard
   with authenticated, signed access. No public URL is created here. */
export const uploadCv = async (file, email) => {
    if (!supabase)
        throw new Error('storage-unavailable');
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${email.replace(/[^a-zA-Z0-9]/g, '_')}/${Date.now()}_${safeName}`;
    const { error } = await supabase.storage.from(CV_BUCKET).upload(path, file, {
        contentType: file.type || 'application/octet-stream',
        upsert: false
    });
    if (error)
        throw new Error('storage-unavailable');
    return `${CV_BUCKET}/${path}`;
};
export const submitTalentApplication = async (record, formType) => {
    const payload = {
        ...record,
        status: 'New Application',
        is_returning: false,
        submittedAt: new Date().toISOString()
    };
    const crmOk = await postToCrm('/applications', { ...payload, form_type: formType });
    if (!crmOk && supabase) {
        const { data: existing } = await supabase
            .from('talent_applications')
            .select('id')
            .eq('email', record.email)
            .limit(1);
        if (existing && existing.length > 0)
            payload.is_returning = true;
        const { error } = await supabase.from('talent_applications').insert([payload]);
        if (error)
            console.warn('Supabase insert error:', error);
    }
    await sendLandingPageEmail(payload, formType);
};
export const submitPartnerEnquiry = async (record) => {
    const domain = String(record.website || '')
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
        .toLowerCase();
    const payload = {
        ...record,
        website_domain: domain,
        status: 'New Partner Enquiry',
        is_returning: false,
        submittedAt: new Date().toISOString()
    };
    const crmOk = await postToCrm('/partners', payload);
    if (!crmOk && supabase) {
        const { data: existing } = await supabase
            .from('partner_enquiries')
            .select('id')
            .or(`website_domain.eq.${domain},email.eq.${record.email}`)
            .limit(1);
        if (existing && existing.length > 0)
            payload.is_returning = true;
        const { error } = await supabase.from('partner_enquiries').insert([payload]);
        if (error)
            console.warn('Supabase insert error:', error);
    }
    await sendLandingPageEmail(payload, 'partner-enquiry');
};
/* General lead intake for the contact form, AI Readiness Assessment and newsletter.
   Same pattern as applications: CRM first when configured, Supabase fallback table
   otherwise, EmailJS notification always (until the CRM takes over notifications). */
export const submitLead = async (record, formType, fallbackTable, crmFormType) => {
    const payload = {
        ...record,
        status: 'New Lead',
        submittedAt: new Date().toISOString()
    };
    const crmOk = await postToCrm('/leads', { ...payload, form_type: crmFormType ?? formType });
    if (!crmOk && supabase) {
        const { error } = await supabase.from(fallbackTable).insert([payload]);
        if (error)
            console.warn('Supabase insert error:', error);
    }
    await sendLandingPageEmail(payload, formType);
};
